import { UserNameEditor } from "./users/UserNameEditor";
import { Link, useParams } from "react-router-dom";
import { Button, Card, Group, Stack, Text } from "@mantine/core";
import { PageHeader } from "@/components/PageHeader";
import { DetailGrid } from "@/components/DetailGrid";
import { JsonExpander } from "@/components/JsonExpander";
import { RelatedList } from "@/components/RelatedList";
import { Mono } from "@/components/Mono";
import { AdminActionButton } from "@/components/AdminActionButton";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { useFirestoreDoc } from "@/firestore/hooks";
import { COLLECTIONS } from "@/firestore/collections";
import type { UserDoc, WithDocId } from "@/firestore/types";
import { fullName } from "@/lib/format";
import { accountDeletionPath, entityListPath } from "@/routes/paths";
import { resetKycUser, setUserRoles } from "@/api/actions";
import { userConfig } from "@/entities/configs/identity";
import { ADMIN_ROLE } from "@/auth/claims";

/** User hub: identity + KYC + the user's wallets (→ Wallet 360) + quick triage lists. */
export function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: user, loading, error } = useFirestoreDoc<UserDoc>(COLLECTIONS.users, id);
  const crumbs = [{ label: "Users", to: entityListPath("user") }];

  if (loading) {
    return (
      <>
        <PageHeader title="User" crumbs={crumbs} />
        <LoadingState />
      </>
    );
  }
  if (error) {
    return (
      <>
        <PageHeader title="User" crumbs={crumbs} />
        <ErrorState error={error} />
      </>
    );
  }
  if (!user) {
    return (
      <>
        <PageHeader title="User" crumbs={crumbs} />
        <EmptyState title="Not found" description={`No user with id ${id}.`} />
      </>
    );
  }

  const u = user as WithDocId<UserDoc>;
  const roles = u.roles ?? [];
  const isAdmin = roles.includes(ADMIN_ROLE);
  const withAdmin = Array.from(new Set([...roles, ADMIN_ROLE]));
  const withoutAdmin = roles.filter((r) => r !== ADMIN_ROLE);

  return (
    <>
      <PageHeader
        title={fullName(u.firstName, u.lastName, u.handle)}
        subtitle={
          <Group gap="xs">
            <Mono value={u.id} />
            {u.primaryEmail ? <Text size="sm">· {u.primaryEmail}</Text> : null}
          </Group>
        }
        crumbs={crumbs}
        actions={
          <Group gap="xs">
            <Button component={Link} to={accountDeletionPath(u.id)} variant="default" size="xs">
              Deletion request
            </Button>
            {isAdmin ? (
              <AdminActionButton
                action={setUserRoles}
                params={{ userId: u.id, roles: withoutAdmin, updatedAt: u.updatedAt }}
              >
                Revoke operator
              </AdminActionButton>
            ) : (
              <AdminActionButton
                action={setUserRoles}
                params={{ userId: u.id, roles: withAdmin, updatedAt: u.updatedAt }}
              >
                Grant operator
              </AdminActionButton>
            )}
            <AdminActionButton action={resetKycUser} params={{ userId: u.id }} />
          </Group>
        }
      />

      <Stack gap="lg">
        <UserNameEditor key={u.id} user={u} />
        <Card withBorder radius="md" padding="lg">
          <DetailGrid fields={userConfig.detailFields(u)} />
        </Card>

        <RelatedList
          spec={{
            title: "Wallets",
            entity: "wallet",
            collection: COLLECTIONS.wallets,
            fkField: "owner",
            value: u.id,
            orderBy: [["createdAt", "desc"]],
          }}
        />
        <RelatedList
          spec={{
            title: "KYC verifications",
            entity: "kycVerification",
            collection: COLLECTIONS.kycVerifications,
            fkField: "user",
            value: u.id,
            orderBy: [["createdAt", "desc"]],
          }}
        />
        {/*
          Owner-key backups, newest first. A rotated user has two rows here side by side — the
          superseded predecessor and the current one — which is the fastest read on "can this
          user recover?". Metadata only; see `OwnerKeyBackupCard` for what is deliberately absent.
        */}
        <RelatedList
          spec={{
            title: "Owner-key backups",
            entity: "ownerKeyBackup",
            collection: COLLECTIONS.ownerKeyBackups,
            fkField: "owner",
            value: u.id,
            orderBy: [["backedUpAt", "desc"]],
          }}
        />
        <RelatedList
          spec={{
            title: "Cards",
            entity: "card",
            collection: COLLECTIONS.cards,
            fkField: "owner",
            value: u.id,
            orderBy: [["createdAt", "desc"]],
          }}
        />
        <RelatedList
          spec={{
            title: "Recent payments",
            entity: "payment",
            collection: COLLECTIONS.payments,
            fkField: "owner",
            value: u.id,
            orderBy: [["approvedAt", "desc"]],
            limit: 20,
          }}
        />

        <RelatedList
          spec={{
            title: "Card refunds",
            entity: "cardRefund",
            collection: COLLECTIONS.cardRefunds,
            fkField: "owner",
            value: u.id,
            orderBy: [["createdAt", "asc"]],
          }}
        />

        <Card withBorder radius="md" padding="md">
          <Text fw={600} mb="xs">
            Raw document
          </Text>
          <JsonExpander value={u} />
        </Card>
      </Stack>
    </>
  );
}
